import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "./useAuth";

export interface AIQuotaStatus {
  tutor_used: number;
  tutor_limit: number;
  tutor_remaining: number;
  quiz_used: number;
  quiz_limit: number;
  quiz_remaining: number;
  isUnlimited: boolean;
  tutorExhausted: boolean;
  quizExhausted: boolean;
}

const DEFAULT: AIQuotaStatus = {
  tutor_used: 0,
  tutor_limit: 5,
  tutor_remaining: 5,
  quiz_used: 0,
  quiz_limit: 1,
  quiz_remaining: 1,
  isUnlimited: false,
  tutorExhausted: false,
  quizExhausted: false,
};

export function useAIQuota() {
  const { data: user } = useAuthUser();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["ai-quota", user?.id],
    queryFn: async (): Promise<AIQuotaStatus> => {
      const { data, error } = await supabase.rpc("get_ai_quota_status", {
        p_user_id: user!.id,
      });
      if (error) throw error;
      const row: any = Array.isArray(data) ? data[0] : data;
      const tl = Number(row?.tutor_limit ?? 5);
      const ql = Number(row?.quiz_limit ?? 1);
      const tu = Number(row?.tutor_used ?? 0);
      const qu = Number(row?.quiz_used ?? 0);
      return {
        tutor_used: tu,
        tutor_limit: tl,
        tutor_remaining: tl === -1 ? -1 : Math.max(tl - tu, 0),
        quiz_used: qu,
        quiz_limit: ql,
        quiz_remaining: ql === -1 ? -1 : Math.max(ql - qu, 0),
        isUnlimited: tl === -1,
        tutorExhausted: tl !== -1 && tu >= tl,
        quizExhausted: ql !== -1 && qu >= ql,
      };
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  return {
    ...(query.data ?? DEFAULT),
    isLoading: query.isLoading,
    refresh: () => queryClient.invalidateQueries({ queryKey: ["ai-quota", user?.id] }),
  };
}
